import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem,
    Select, Typography,
} from '@mui/material';

import { resultsToSvg } from '../utils/resultsFigure';
import { svgToPng } from '../utils/networkExport';
import { downloadBlob } from '../utils/zip';
import { exportStamp } from '../utils/neighborExport';

const TOP_N_OPTIONS = [1, 2, 3, 4, 5];

/**
 * Dialog that previews the results as a figure and downloads it as SVG or PNG.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {() => void} props.onClose - Close handler.
 * @param {Array<{proteinName: string, items: Array<object>}>} props.groups - Protein groups in display order.
 * @param {string} props.jobId - Job ID, shown in the figure subtitle.
 * @param {string|null} props.modelLabel - Name of the model that made the predictions, or null if unknown.
 * @param {string|null} props.modelFileLine - Which model file exactly (Zenodo record, md5), or null if unknown.
 * @param {string} props.orderLabel - How the groups are ordered, shown in the figure subtitle.
 * @returns {React.ReactElement} - The dialog.
 */
const ResultsFigureDialog = ({ open, onClose, groups, jobId, modelLabel, modelFileLine, orderLabel }) => {
    const theme = useTheme();
    const [topN, setTopN] = useState(3);

    const domainCount = groups.reduce((n, g) => n + g.items.length, 0);

    const svg = useMemo(() => {
        if (!open) return '';
        return resultsToSvg({
            groups,
            topN,
            caption: {
                title: modelLabel ? `Substrate predictions by ${modelLabel}` : 'Substrate predictions (model not recorded)',
                subtitle: [
                    `${domainCount} domain${domainCount === 1 ? '' : 's'} across ${groups.length} protein${groups.length === 1 ? '' : 's'}`,
                    `order: ${orderLabel}`,
                    `top ${topN} prediction${topN === 1 ? '' : 's'} per domain`,
                    `job ${jobId}`,
                ].join('; '),
                footnote: modelFileLine ? `Model file: ${modelFileLine}` : null,
            },
            colors: {
                background: theme.palette.background.paper,
                text: theme.palette.text.primary,
                textSecondary: theme.palette.text.secondary,
                grid: theme.palette.surface.borderStrong,
                topBar: theme.palette.primary.main,
                otherBar: theme.palette.surface.borderStrong,
                domainFill: theme.palette.accent.main,
                domainText: theme.palette.accent.contrastText,
                bands: theme.palette.geneBands,
            },
        });
    }, [open, groups, topN, domainCount, modelLabel, modelFileLine, orderLabel, jobId, theme]);

    const downloadSvg = () => {
        downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), `parasect-results-${exportStamp()}.svg`);
    };

    const downloadPng = async () => {
        try {
            downloadBlob(await svgToPng(svg), `parasect-results-${exportStamp()}.png`);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
            <DialogTitle>Download figure</DialogTitle>
            <DialogContent>
                <Typography variant='body2' color='textSecondary' gutterBottom>
                    Domains are laid out left to right in the order shown on the results page, grouped per
                    protein. Each domain shows its top predictions as bars. Colours follow the current light or
                    dark mode.
                </Typography>
                <FormControl size='small' sx={{ mt: 1, mb: 2, minWidth: 200 }}>
                    <InputLabel id='figure-top-n-label'>Predictions per domain</InputLabel>
                    <Select
                        labelId='figure-top-n-label'
                        label='Predictions per domain'
                        value={topN}
                        onChange={(e) => setTopN(e.target.value)}
                    >
                        {TOP_N_OPTIONS.map((n) => (
                            <MenuItem key={n} value={n}>Top {n}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box
                    sx={{
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        maxHeight: '60vh',
                        '& svg': { display: 'block' },
                    }}
                    // the SVG is generated by resultsToSvg, which escapes every piece of text it writes
                    dangerouslySetInnerHTML={{ __html: svg.replace(/^<\?xml[^>]*>\s*/, '') }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button variant='outlined' onClick={downloadSvg} disabled={!svg}>Download SVG</Button>
                <Button variant='contained' onClick={downloadPng} disabled={!svg}>Download PNG</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResultsFigureDialog;
